import { useCallback, useEffect } from "react";
import { useRevalidator } from "react-router-dom";

import Button from "components/Button";
import SubmitButton from "components/SubmitButton";
import { ErrorMessage } from "components/Modal/ErrorMessage";
import TaggedText from "components/TaggedText";

import Modal, { ModalButtonsSection, ModalContent, ModalSections } from "v2/components/billing/modals";
import WarningBox from "components/WarningBox";

import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";

import { PaymentSourceActions, PaymentSourceInputName } from "v2/dataroutes/BillingData";
import { PAYMENT_SOURCES_FETCHER_HREF, usePaymentSources } from "v2/pages/billing/hooks/usePaymentSources";

import { getPaymentSourceTitle, PaymentSource } from "types/billing/PaymentSource";
import { useUser } from "routes/dataroutes/UserData";

import { getErrorFromFetcherData } from "utils/fetcherUtils";
import { fmtDate, fmtNumber } from "utils";
import { trackBillingV2ModalOpened, trackPaymentMethodRemoveSubmitted, trackPaymentMethodRemoveSucceeded } from "utils/Tracking";


export default function RemovePrimaryPaymentSourceModal(
  {
    paymentSource,
    onClose,
  }: {
    paymentSource: PaymentSource,
    onClose: () => void,
  }
) {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();
  const { paymentSourcesFetcher } = usePaymentSources();
  const revalidator = useRevalidator();

  const isSubmitting = paymentSourcesFetcher.state !== "idle" &&
    paymentSourcesFetcher.formData?.get(PaymentSourceInputName.action) === PaymentSourceActions.remove_primary_with_cancel.value;

  // Refresh the active subscription so the cancellation banner / manage-plan card reflect
  // the new scheduledSubscription state. Runs only after a successful submission.
  const onSuccess = useCallback(() => {
    trackPaymentMethodRemoveSucceeded({ method_type: paymentSource.type, was_primary: true });
    // TODO should not need a full revalidation here
    revalidator.revalidate();
    onClose();
  }, [ revalidator, onClose, paymentSource.type ]);

  useCallbackAfterSubmission(onSuccess, paymentSourcesFetcher);

  // TODO no need to wrap onClose here, useCloseModal does have a way to prevent closing, that's exactly for cases like this below
  const close = useCallback(() => {
    if (isSubmitting) return;
    return onClose();
  }, [ isSubmitting, onClose ]);

  useEffect(() => {
    trackBillingV2ModalOpened('remove_primary_payment_method');
  }, []);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [ close ]);

  // Read error strictly from this fetcher's data — avoid useFormattedActionError's
  // route-actionData fallback, which could surface a stale error from an unrelated
  // prior route action when the modal first opens.
  const fetcherError = getErrorFromFetcherData(paymentSourcesFetcher);
  const errorMessage = fetcherError?.taggedMessage
    ? <TaggedText message={ fetcherError.taggedMessage.message } linkStyle={ fetcherError.taggedMessage.linkStyle }/>
    : fetcherError?.message;
  const termEnd = activeSubscription?.currentTermEnd;

  return (
    <Modal
      headline="Are you sure you wish to delete your primary payment card?"
      wrapHeadline={ false }
      onClose={ close }
    >
      <paymentSourcesFetcher.Form method="POST" action={ PAYMENT_SOURCES_FETCHER_HREF }>
        <input type="hidden" name={ PaymentSourceInputName.id } value={ paymentSource.id }/>
        <ModalContent>
          <ModalSections>
            <div>
              Removing <span className="font-semibold">{ getPaymentSourceTitle(paymentSource) }</span> will schedule your subscription
              for cancellation at the end of your current billing period
              { termEnd ? <span className="font-semibold">{ fmtDate(new Date(termEnd * 1000)) }</span>  : null }.
            </div>
            <WarningBox
              variant="warning"
              title="What happens next"
              items={ [
                { text: "You keep access to your current plan until the end of the billing period." },
                { text: user?.isBannedFromFree
                    ? "After that, your subscription will be cancelled and your API key will be removed."
                    : <>After that, your account will move to the <span className="font-semibold">Free</span> plan with { fmtNumber(1000) } API credits per month.</>
                },
              ] }
            />
            <ErrorMessage errorMessage={ errorMessage } closeModalCallback={ close }/>
          </ModalSections>
          <ModalButtonsSection>
            <Button
              text="Keep payment method"
              className="button button-secondary w-full"
              size="MD"
              onClick={ close }
              disabled={ isSubmitting }
            />
            <SubmitButton
              text="Remove payment method"
              className="button button-primary destructive w-full"
              size="MD"
              fetchersToCheck={ [ paymentSourcesFetcher ] }
              onClick={ () => trackPaymentMethodRemoveSubmitted(paymentSource.type) }
              { ...PaymentSourceActions.remove_primary_with_cancel }
            />
          </ModalButtonsSection>
        </ModalContent>
      </paymentSourcesFetcher.Form>
    </Modal>
  );
}
